import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Briefcase, Clock, Star, Globe, Smartphone, Code, Palette,
  ArrowRight, Plus, Users, CheckCircle, Send, X, Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectBoard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    project_title: '',
    project_description: '',
    project_type: '',
    timeline: '',
    budget_range: '',
    is_donor: false,
    donation_reference: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/projects/submit`, formData);
      toast.success('Project submitted! We\'ll review it shortly.');
      setShowSubmitModal(false);
      setFormData({
        business_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        website_url: '',
        project_title: '',
        project_description: '',
        project_type: '',
        timeline: '',
        budget_range: '',
        is_donor: false,
        donation_reference: ''
      });
    } catch (error) {
      toast.error('Failed to submit project');
    } finally {
      setSubmitting(false);
    }
  };

  const getProjectTypeIcon = (type) => {
    switch (type) {
      case 'website': return Globe;
      case 'webapp': return Code;
      case 'mobile': return Smartphone;
      default: return Palette;
    }
  };

  const getTimelineLabel = (timeline) => {
    switch (timeline) {
      case 'urgent': return { label: 'Urgent', color: 'text-red-400' };
      case '1-2 weeks': return { label: '1-2 Weeks', color: 'text-yellow-400' };
      case '1 month': return { label: '1 Month', color: 'text-[#00AEEF]' };
      case 'flexible': return { label: 'Flexible', color: 'text-[#00FFE0]' };
      default: return { label: timeline, color: 'text-[#94A3B8]' };
    }
  };

  const inputClass = "w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-3 focus:border-[#FF6A00] focus:outline-none";
  const labelClass = "block text-sm text-[#94A3B8] mb-2";

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-[#00AEEF]/10 via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-20 h-20 rounded-full bg-[#00AEEF]/20 flex items-center justify-center mx-auto mb-6">
            <Briefcase className="w-10 h-10 text-[#00AEEF]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-tight mb-4">
            Student Project Board
          </h1>
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto mb-8">
            Need a website built? Our students are ready to help! Post your project and give them real-world experience 
            while getting quality work done.
          </p>
          <button
            onClick={() => setShowSubmitModal(true)}
            data-testid="submit-project-btn"
            className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all"
          >
            <Plus className="w-5 h-5" />
            Submit Your Project
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 bg-[#0A1F44]/30">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#FF6A00]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#FF6A00] font-bold">1</span>
              </div>
              <h3 className="font-bold text-white mb-1">Submit Project</h3>
              <p className="text-sm text-[#94A3B8]">Describe what you need</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#00AEEF]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#00AEEF] font-bold">2</span>
              </div>
              <h3 className="font-bold text-white mb-1">We Review</h3>
              <p className="text-sm text-[#94A3B8]">Our team approves projects</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#00FFE0] font-bold">3</span>
              </div>
              <h3 className="font-bold text-white mb-1">Students Apply</h3>
              <p className="text-sm text-[#94A3B8]">Qualified students apply</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#FFD700] font-bold">4</span>
              </div>
              <h3 className="font-bold text-white mb-1">Get It Built</h3>
              <p className="text-sm text-[#94A3B8]">Student builds your project</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects List */}
      <section className="py-16 bg-[#060F22]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white">Available Projects</h2>
            <span className="text-[#94A3B8]">{projects.length} projects</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
            </div>
          ) : projects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => {
                const IconComponent = getProjectTypeIcon(project.project_type);
                const timeline = getTimelineLabel(project.timeline);
                
                return (
                  <div 
                    key={project.id}
                    className={`bg-[#0A1F44]/50 border rounded-xl p-6 hover:border-[#00AEEF]/50 transition-all ${
                      project.is_featured 
                        ? 'border-[#FFD700]/50 ring-1 ring-[#FFD700]/20' 
                        : 'border-[#00AEEF]/20'
                    }`}
                  >
                    {project.is_featured && (
                      <div className="flex items-center gap-1 text-[#FFD700] text-sm mb-3">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="font-medium">Featured - Donor Project</span>
                      </div>
                    )}
                    
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        project.is_featured ? 'bg-[#FFD700]/20' : 'bg-[#00AEEF]/20'
                      }`}>
                        <IconComponent className={`w-6 h-6 ${project.is_featured ? 'text-[#FFD700]' : 'text-[#00AEEF]'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{project.project_title}</h3>
                        <p className="text-sm text-[#94A3B8]">{project.business_name}</p>
                      </div>
                    </div>

                    <p className="text-[#94A3B8] text-sm mb-4 line-clamp-3">{project.project_description}</p>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-4 h-4 text-[#94A3B8]" />
                        <span className={timeline.color}>{timeline.label}</span>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-[#060F22] text-[#94A3B8] capitalize">
                        {project.project_type}
                      </span>
                    </div>

                    {project.status === 'in_progress' && project.assigned_student ? (
                      <div className="flex items-center gap-2 text-[#00FFE0] text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>In Progress - Assigned to {project.assigned_student.name}</span>
                      </div>
                    ) : (
                      <Link
                        to={`/student/login?redirect=/projects/${project.id}`}
                        className="inline-flex items-center gap-2 text-[#FF6A00] hover:text-[#FF6A00]/80 font-medium text-sm"
                      >
                        <Users className="w-4 h-4" />
                        Login to Apply
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl">
              <Briefcase className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Projects Yet</h3>
              <p className="text-[#94A3B8] mb-6">Be the first to submit a project for our students!</p>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-full px-6 py-3"
              >
                <Plus className="w-5 h-5" />
                Submit a Project
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Donor Benefits */}
      <section className="py-16 bg-[#0A1F44]/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#FFD700]/10 to-[#FF6A00]/10 border border-[#FFD700]/30 rounded-2xl p-8 sm:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                <Heart className="w-7 h-7 text-[#FFD700]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Donor Benefits</h2>
                <p className="text-[#94A3B8]">Support our students and get priority service</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                <span className="text-white">Featured project listing</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                <span className="text-white">Priority review & matching</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                <span className="text-white">Access to top students</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                <span className="text-white">Tax-deductible contribution</span>
              </div>
            </div>

            <Link
              to="/donate"
              className="inline-flex items-center gap-2 bg-[#FFD700] text-[#0A1F44] font-bold rounded-full px-6 py-3 hover:shadow-[0_0_20px_rgba(255,215,0,0.3)] transition-all"
            >
              <Heart className="w-5 h-5" />
              Become a Donor
            </Link>
          </div>
        </div>
      </section>

      {/* Submit Project Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20 sticky top-0 bg-[#0A1F44] z-10">
              <div>
                <h2 className="text-xl font-bold text-white">Submit Your Project</h2>
                <p className="text-[#94A3B8] text-sm">Fill out the form to post your project</p>
              </div>
              <button onClick={() => setShowSubmitModal(false)} className="text-[#94A3B8] hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Business Info */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Business Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Business Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      data-testid="project-business-name"
                      className={inputClass}
                      placeholder="Your business name"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Contact Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      data-testid="project-contact-name"
                      className={inputClass}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      data-testid="project-contact-email"
                      className={inputClass}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Phone (optional)</label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className={inputClass}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Project Title *</label>
                    <input
                      type="text"
                      required
                      value={formData.project_title}
                      onChange={(e) => setFormData({ ...formData, project_title: e.target.value })}
                      data-testid="project-title"
                      className={inputClass}
                      placeholder="e.g., Business Website Redesign"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Project Description *</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.project_description}
                      onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                      data-testid="project-description"
                      className={`${inputClass} resize-none`}
                      placeholder="Describe what you need built, features required, etc."
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Project Type *</label>
                      <select
                        required
                        value={formData.project_type}
                        onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                        data-testid="project-type"
                        className={inputClass}
                      >
                        <option value="">Select type</option>
                        <option value="website">Website</option>
                        <option value="webapp">Web Application</option>
                        <option value="mobile">Mobile App</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Timeline *</label>
                      <select
                        required
                        value={formData.timeline}
                        onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                        data-testid="project-timeline"
                        className={inputClass}
                      >
                        <option value="">Select timeline</option>
                        <option value="urgent">Urgent (ASAP)</option>
                        <option value="1-2 weeks">1-2 Weeks</option>
                        <option value="1 month">1 Month</option>
                        <option value="flexible">Flexible</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Current Website (if any)</label>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      className={inputClass}
                      placeholder="https://yoursite.com"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Budget Range (optional)</label>
                    <select
                      value={formData.budget_range}
                      onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Select range</option>
                      <option value="volunteer">Volunteer/Free</option>
                      <option value="$100-$500">$100 - $500</option>
                      <option value="$500-$1000">$500 - $1,000</option>
                      <option value="$1000+">$1,000+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Donor Section */}
              <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_donor}
                    onChange={(e) => setFormData({ ...formData, is_donor: e.target.checked })}
                    data-testid="project-is-donor"
                    className="mt-1 w-5 h-5 rounded border-[#FFD700]/30 text-[#FFD700] focus:ring-[#FFD700]"
                  />
                  <div>
                    <span className="text-white font-medium">I'm a donor to NextGen Tech Pathways</span>
                    <p className="text-[#94A3B8] text-sm">Your project will be featured and prioritized!</p>
                  </div>
                </label>
                {formData.is_donor && (
                  <div className="mt-4">
                    <label className={labelClass}>Donation Reference (optional)</label>
                    <input
                      type="text"
                      value={formData.donation_reference}
                      onChange={(e) => setFormData({ ...formData, donation_reference: e.target.value })}
                      className={inputClass}
                      placeholder="Transaction ID, email used, or approximate date"
                    />
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-3 rounded-xl border border-[#00AEEF]/20 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-project-form-btn"
                  className="flex-1 bg-[#FF6A00] text-white font-bold px-4 py-3 rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Project
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectBoard;
